// Who asked for this quote, read from the quote request (RFQ) text alone.
//
// Peter, 2026-10-13: "Make sure that you update QuoteMaker as needed, so it
// knows how to link RFQs and hence Quotes to the correct Firm and
// Requester(s)."
//
// A strata firm sends a quote request through one or more strata managers.
// This module finds those people (name, title, email, phone) in the request
// text with labelled-field and signature rules. It is pure and deterministic:
// no AI, no network, no storage. The AI contact extraction is the fallback and
// is merged in by `finaliseContactExtraction`, which also applies the email
// guard: an email that is not printed in the source text was invented, and an
// email on the builder's own domain is never the client's.
//
// Peter's rule, 2026-10-13: "file names shouldnt dictate anything. only the
// contents." The file name (`doc`) is carried as provenance only. Nothing in
// this module reads it to decide a role, a firm, a layout or a classification.

export type RfqRequesterRole = 'order_by' | 'strata_manager' | 'assistant' | 'contact' | 'issued_by';

export interface RfqRequesterSource {
    /** File name of the document the requester was read from. Provenance only. */
    doc?: string;
    /** The label or rule that found the requester, e.g. "Body Corp Manager". */
    label: string;
}

export interface RfqRequester {
    name: string;
    title: string | null;
    email: string | null;
    phone: string | null;
    role: RfqRequesterRole;
    source: RfqRequesterSource;
}

export interface RequesterTenantContext {
    /** The builder's own email domains. Never a client's. */
    tenantDomains?: readonly string[] | null;
    /** The builder's own staff. Never a requester. */
    tenantStaffNames?: readonly string[] | null;
    tenantStaffEmails?: readonly string[] | null;
    /** The builder's legal and trading names. Never the requesting firm. */
    tenantNames?: readonly string[] | null;
    tenantAbns?: readonly string[] | null;
}

export interface ExtractRfqRequestersOptions extends RequesterTenantContext {
    /** File name of the source document. Provenance only. */
    doc?: string | null;
}

export interface RfqFirmHints {
    firmNames: string[];
    abns: string[];
    emailDomains: string[];
    formFingerprint: string | null;
}

const ROLES: ReadonlySet<string> = new Set(['order_by', 'strata_manager', 'assistant', 'contact', 'issued_by']);

const EMAIL_SOURCE = "[A-Za-z0-9._%+'-]+@[A-Za-z0-9-]+(?:\\.[A-Za-z0-9-]+)*\\.[A-Za-z]{2,}";
const AU_PHONE_SOURCE = '(?:\\+?61[\\s-]?\\(?0?\\)?\\s?|\\(?0)[2-478]\\)?(?:[\\s-]?\\d){8}';

const GENERIC_MAILBOXES: ReadonlySet<string> = new Set([
    'info', 'office', 'admin', 'administration', 'reception', 'repairs', 'repair', 'maintenance',
    'accounts', 'account', 'enquiries', 'enquiry', 'inquiries', 'quotes', 'quote', 'strata', 'mail',
    'contact', 'hello', 'support', 'noreply', 'no-reply', 'donotreply', 'workorders', 'workorder',
    'orders', 'jobs', 'service', 'services', 'team', 'operations', 'trades', 'contractors', 'invoices',
]);

const FREE_MAIL_DOMAINS: ReadonlySet<string> = new Set([
    'gmail.com', 'googlemail.com', 'yahoo.com', 'yahoo.com.au', 'hotmail.com', 'hotmail.com.au',
    'outlook.com', 'outlook.com.au', 'live.com', 'live.com.au', 'icloud.com', 'me.com', 'aol.com',
    'bigpond.com', 'bigpond.net.au', 'optusnet.com.au', 'protonmail.com', 'proton.me', 'msn.com',
    'tpg.com.au', 'iinet.net.au',
]);

// Words that never appear in a person's name on an RFQ. A label value that
// contains one is a firm, an address, a heading or another field.
const NAME_STOPWORDS: ReadonlySet<string> = new Set([
    'strata', 'plan', 'manager', 'managers', 'management', 'pty', 'ltd', 'limited', 'owners', 'owner',
    'corporation', 'corp', 'corporate', 'body', 'request', 'quote', 'quotation', 'order', 'no',
    'number', 'services', 'service', 'group', 'street', 'st', 'road', 'rd', 'avenue', 'ave', 'the',
    'of', 'and', 'for', 'building', 'builder', 'contractor', 'repairs', 'repair', 'maintenance',
    'property', 'common', 'unit', 'lot', 'phone', 'email', 'date', 'attention', 'attn', 'client',
    'australia', 'nsw', 'vic', 'qld', 'act', 'tas', 'wa', 'sa', 'nt', 'job', 'work', 'site', 'contact',
    'enquiries', 'issued', 'by', 'agent', 'assistant', 'senior', 'community', 'office', 'team',
    'department', 'accounts', 'admin', 'reception', 'invoice', 'details', 'description', 'scope',
    'urgent', 'priority', 'committee', 'chairperson', 'secretary', 'treasurer', 'regards', 'kind',
    'thanks', 'thank', 'you', 'dear', 'hi', 'hello', 'sir', 'madam', 'page', 'total', 'gst', 'abn',
    'acn', 'bsb', 'account', 'bank', 'partners', 'solutions', 'specialists', 'realty', 'estate',
    'tbc', 'tba', 'na', 'nil', 'none', 'unknown', 'reference', 'ref', 'mobile', 'fax', 'address',
    'access', 'name', 'tel', 'ph', 'due', 'lots', 'billing', 'see', 'above', 'suite', 'level', 'box',
    'po', 'please', 'note', 'parade', 'crescent', 'drive',
]);
const STATE_CODES: ReadonlySet<string> = new Set(['nsw', 'vic', 'qld', 'sa', 'wa', 'tas', 'nt', 'act']);
const HONORIFICS: ReadonlySet<string> = new Set(['mr', 'mrs', 'ms', 'miss', 'dr']);
const LOGIN_NAME_RE = /^[A-Za-z][A-Za-z'’-]+(?:\.[A-Za-z][A-Za-z'’-]+)+$/;
const MIXED_NAME_TOKEN_RE = /^(?:[A-Z][a-z][A-Za-z'’-]*|[A-Z]['’][A-Z][a-z][A-Za-z'’-]*)$/;
const CAPS_NAME_TOKEN_RE = /^[A-Z][A-Z'’-]+$/;
const INITIAL_RE = /^[A-Z]\.?$/;
// A column gap in the pdf text layer (tab, two spaces, " | ") ends a value.
const COLUMN_GAP_RE = /\t|\s{2,}|\s\|\s/g;
const COLUMN_GAP = '¦';
const NAME_PARTICLES: ReadonlySet<string> = new Set(['van', 'von', 'de', 'da', 'di', 'du', 'del', 'della', 'le', 'la', 'bin', 'binti', 'al']);

interface RequesterLabelSpec {
    pattern: string;
    label: string;
    role: RfqRequesterRole;
    /** Title implied by the label. 'printed' = the label text itself. */
    title: string | null | 'printed';
}

// Longest first: at one position the regex alternation takes the first
// alternative that matches, so "Agent Strata Manager:" is never read as
// "Strata Manager:".
const REQUESTER_LABELS: readonly RequesterLabelSpec[] = [
    { pattern: 'agent\\s+strata\\s+manager', label: 'Agent Strata Manager', role: 'strata_manager', title: 'Strata Manager' },
    { pattern: 'assistant\\s+strata\\s+manager', label: 'Assistant Strata Manager', role: 'assistant', title: 'Assistant Strata Manager' },
    { pattern: 'senior\\s+strata\\s+manager', label: 'Senior Strata Manager', role: 'strata_manager', title: 'Senior Strata Manager' },
    { pattern: 'body\\s+corp(?:orate)?\\.?\\s+manager', label: 'Body Corp Manager', role: 'strata_manager', title: 'printed' },
    { pattern: 'strata\\s+manager', label: 'Strata Manager', role: 'strata_manager', title: 'Strata Manager' },
    { pattern: 'job\\s+issued\\s+by', label: 'Job Issued By', role: 'issued_by', title: null },
    { pattern: 'issued\\s+by', label: 'Issued By', role: 'issued_by', title: null },
    { pattern: 'order(?:ed)?\\s+by', label: 'Order By', role: 'order_by', title: null },
    { pattern: 'requested\\s+by', label: 'Requested By', role: 'order_by', title: null },
    { pattern: 'contact\\s+for\\s+enquiries', label: 'Contact for enquiries', role: 'contact', title: null },
];

/**
 * The source labels of a requester the request itself names as one: printed
 * under a requester label, the person in the client fields, or an intake
 * contact. Not 'AI extraction' or 'Unknown'. A person filed under one of these
 * stays a requester even when they also let the builder in (the extraction's
 * rule below, isSiteOnly; project-requesters.ts isVouchedRequester).
 */
export const REQUESTER_SOURCE_LABELS: ReadonlySet<string> = new Set([
    ...REQUESTER_LABELS.map((spec) => spec.label),
    'Quote requester',
    'Intake contact',
]);

// Other form labels. They end a requester's value when pdf text joins columns
// onto one line ("Order By: Jane Citizen Order No: 1234"), and they keep the
// label/value queue aligned when the layout prints labels before values.
const OTHER_FIELD_LABELS: readonly string[] = [
    'order\\s+no\\.?', 'order\\s+number', 'quote\\s+no\\.?', 'quote\\s+number', 'quote\\s+ref(?:erence)?',
    'strata\\s+plan(?:\\s+no\\.?)?', 'plan\\s+no\\.?', 's\\.?p\\.?\\s+no\\.?', 'job\\s+no\\.?', 'job\\s+number',
    'work\\s+order(?:\\s+no\\.?)?', 'date(?:\\s+issued)?', 'due\\s+date', 'telephone', 'phone', 'ph', 'tel',
    'mobile', 'mob', 'fax', 'e-?mail', 'abn', 'site\\s+address', 'property\\s+address', 'property\\s+affected',
    'property', 'address', 'site\\s+contacts?', 'contact\\s+name', 'contact', 'bill\\s+to', 'managing\\s+agent',
    'contractor(?:\\s+name)?', 'priority', 'reference', 'ref', 'lot', 'unit', 'building', 'attention', 'attn',
    'job\\s+description', 'description', 'special\\s+instructions', 'access', 'owner', 'tenant', 'category',
    'status', 'invoice\\s+to',
    // GK-style form labels (2026-10-13 real-text check).
    'attention\\s+to', 'order\\s+date', 'quote\\s+due(?:\\s+by)?', 'no\\.?\\s+of\\s+lots', 'billing\\s+address',
    'issued\\s+date', 'access\\s+contact', 'tel-?[hw]', 'job\\s+address', 'job\\s+details', 'please\\s+note',
];

const REQUESTER_LABEL_RE = new RegExp(
    `\\b(${REQUESTER_LABELS.map((spec) => spec.pattern).join('|')})\\s*:`,
    'gi',
);
const ANY_LABEL_SOURCE = [...REQUESTER_LABELS.map((spec) => spec.pattern), ...OTHER_FIELD_LABELS].join('|');
const ANY_LABEL_GLOBAL_SOURCE = `\\b(?:${ANY_LABEL_SOURCE})\\s*:`;
const NEXT_LABEL_RE = new RegExp(`\\s\\b(?:${ANY_LABEL_SOURCE})\\s*:`, 'i');
const NEXT_REQUESTER_LABEL_RE = new RegExp(`\\s\\b(?:${REQUESTER_LABELS.map((spec) => spec.pattern).join('|')})\\s*:`, 'i');

const TITLE_CORE = '(?:(?:senior|assistant|associate|trainee|licensed|portfolio)\\s+)?'
    + '(?:strata(?:\\s+community)?|body\\s+corp(?:orate)?\\.?|community(?:\\s+association)?|portfolio)\\s+manager(?:\\s+assistant)?'
    + '|strata\\s+(?:assistant|administrator|officer|coordinator)'
    + '|assistant\\s+to\\s+(?:the\\s+)?strata\\s+manager';
const TITLE_RE = new RegExp(`^(?:${TITLE_CORE})$`, 'i');
const TITLE_LINE_RE = new RegExp(`^(${TITLE_CORE})(?:\\s*(?:\\||-|–|,|@|\\bat\\b)\\s*(.+))?$`, 'i');
const NAME_THEN_TITLE_RE = new RegExp(`^(.+?)\\s*(?:\\||-|–|,)\\s*(${TITLE_CORE})(?:\\s*(?:\\||-|–|,)\\s*.*)?$`, 'i');

// ---------------------------------------------------------------------------
// Small text helpers
// ---------------------------------------------------------------------------

function cleanText(value: unknown): string {
    if (typeof value !== 'string') return '';
    return value.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '').replace(/\s+/g, ' ').trim();
}

function titleCaseWord(word: string): string {
    return word.split(/([-'’])/).map((part) => (
        /^[-'’]$/.test(part) ? part : part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
    )).join('');
}

function titleCase(value: string): string {
    return value.split(' ').map(titleCaseWord).join(' ');
}

function lower(values: readonly string[] | null | undefined): string[] {
    return (Array.isArray(values) ? values : [])
        .map((value) => cleanText(value).toLowerCase())
        .filter(Boolean);
}

/** "JANE CITIZEN", "jane.citizen", "Ms Jane Citizen" all become "Jane Citizen". */
export function normaliseRequesterName(raw: unknown): string {
    let value = cleanText(raw).replace(/^(?:mr|mrs|ms|miss|dr)\.?\s+/i, '');
    if (/^[A-Za-z][A-Za-z'’-]*(?:\.[A-Za-z][A-Za-z'’-]*)+$/.test(value)) {
        value = value.split('.').join(' ');
    }
    if (value && (value === value.toUpperCase() || value === value.toLowerCase())) {
        value = titleCase(value);
    }
    return value;
}

/** The key two spellings of the same person share. */
export function requesterNameKey(name: unknown): string {
    return cleanText(name).toLowerCase()
        .replace(/[.]/g, ' ')
        .replace(/['’-]/g, '')
        .replace(/[^a-z\s]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

export function sameRequesterName(left: unknown, right: unknown): boolean {
    const a = requesterNameKey(left);
    return !!a && a === requesterNameKey(right);
}

function looksLikePersonName(value: string): boolean {
    if (!value || value.length > 60 || /[0-9@:/\\]/.test(value)) return false;
    const tokens = value.split(' ');
    if (tokens.length < 2 || tokens.length > 4) return false;
    return tokens.every((token, index) => {
        const bare = token.toLowerCase().replace(/[^a-z]/g, '');
        if (!bare || NAME_STOPWORDS.has(bare)) return false;
        const inner = index > 0 && index < tokens.length - 1;
        if (inner && /^[A-Z]\.?$/.test(token)) return true;
        if (index > 0 && NAME_PARTICLES.has(token.toLowerCase())) return true;
        return /^[A-Z][A-Za-z'’-]*[A-Za-z]$/.test(token);
    });
}

function displayTitle(raw: string): string {
    return titleCase(cleanText(raw).replace(/\.$/, '').toLowerCase());
}

const ASSISTANT_TITLE_RE = /assistant|administrator|officer|coordinator/i;

/**
 * The role a title implies. The person who ordered or issued the request
 * keeps that role whatever their title (an assistant or an administrator can
 * place the order): the quote email goes to the Order By person.
 */
/** The role a printed title implies ("Assistant Strata Manager"), kept when the RFQ named the role itself. */
export function roleFromTitle(title: string | null, fallback: RfqRequesterRole): RfqRequesterRole {
    if (!title) return fallback;
    if (fallback === 'order_by' || fallback === 'issued_by') return fallback;
    if (ASSISTANT_TITLE_RE.test(title)) return 'assistant';
    if (fallback === 'contact' && /manager/i.test(title)) return 'strata_manager';
    return fallback;
}

function emailMatches(text: string): Array<{ email: string; index: number }> {
    const re = new RegExp(EMAIL_SOURCE, 'g');
    const found: Array<{ email: string; index: number }> = [];
    let match: RegExpExecArray | null;
    while ((match = re.exec(text)) !== null) {
        found.push({ email: match[0].replace(/[.]+$/, ''), index: match.index });
    }
    return found;
}

function firstPhone(text: string): { phone: string; index: number } | null {
    const match = new RegExp(AU_PHONE_SOURCE).exec(text);
    if (!match) return null;
    return { phone: match[0].trim(), index: match.index };
}

function splitEmail(email: string): { local: string; domain: string } {
    const at = email.lastIndexOf('@');
    if (at < 0) return { local: '', domain: '' };
    return { local: email.slice(0, at).toLowerCase(), domain: email.slice(at + 1).toLowerCase() };
}

function domainIsTenant(domain: string, tenantDomains: readonly string[]): boolean {
    return tenantDomains.some((tenant) => domain === tenant || domain.endsWith(`.${tenant}`));
}

function isGenericMailbox(email: string): boolean {
    return GENERIC_MAILBOXES.has(splitEmail(email).local);
}

// ---------------------------------------------------------------------------
// The email guard
// ---------------------------------------------------------------------------

/**
 * True when `email` is printed verbatim (ignoring case) in `sourceText`, as a
 * whole address: "ane.citizen@firm.com.au" is not accepted because
 * "jane.citizen@firm.com.au" is printed.
 */
export function emailAppearsInSource(email: unknown, sourceText: unknown): boolean {
    const needle = cleanText(email).replace(/^mailto:/i, '').toLowerCase();
    const haystack = typeof sourceText === 'string' ? sourceText.toLowerCase() : '';
    if (!needle || !needle.includes('@') || !haystack) return false;
    let from = 0;
    for (;;) {
        const index = haystack.indexOf(needle, from);
        if (index < 0) return false;
        const before = index > 0 ? haystack.charAt(index - 1) : '';
        const after = haystack.charAt(index + needle.length);
        const afterNext = haystack.charAt(index + needle.length + 1);
        const boundaryBefore = !before || !/[a-z0-9._%+'-]/.test(before);
        const boundaryAfter = !after || (!/[a-z0-9_-]/.test(after) && !(after === '.' && /[a-z0-9]/.test(afterNext)));
        if (boundaryBefore && boundaryAfter) return true;
        from = index + 1;
    }
}

/** True for the builder's own addresses: its domains (and subdomains) and staff emails. */
export function isTenantEmail(email: unknown, context: RequesterTenantContext = {}): boolean {
    const value = cleanText(email).replace(/^mailto:/i, '').toLowerCase();
    if (!value.includes('@')) return false;
    if (lower(context.tenantStaffEmails).includes(value)) return true;
    return domainIsTenant(splitEmail(value).domain, lower(context.tenantDomains));
}

/**
 * The single acceptance rule for an extracted client or requester email: well
 * formed, printed in the source, and not the builder's own. Returns the email
 * lowercased, or null.
 */
export function acceptExtractedEmail(
    email: unknown,
    sourceText: unknown,
    context: RequesterTenantContext = {},
): string | null {
    const value = cleanText(email).replace(/^mailto:/i, '').replace(/[.,;:]+$/, '');
    if (!value || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return null;
    if (!emailAppearsInSource(value, sourceText)) return null;
    if (isTenantEmail(value, context)) return null;
    return value.toLowerCase();
}

// ---------------------------------------------------------------------------
// Matching people to emails
// ---------------------------------------------------------------------------

function nameParts(name: string): { first: string; last: string } | null {
    const parts = requesterNameKey(name).split(' ').map((part) => part.replace(/[^a-z]/g, '')).filter(Boolean);
    if (parts.length < 2) return null;
    return { first: parts[0] as string, last: parts[parts.length - 1] as string };
}

/** jane.citizen@, janecitizen@, jcitizen@, citizenj@ ... belong to Jane Citizen. */
function emailLocalStronglyMatchesName(local: string, name: string): boolean {
    const parts = nameParts(name);
    if (!parts) return false;
    const bare = local.toLowerCase().replace(/[^a-z]/g, '');
    const { first, last } = parts;
    return [
        first + last, last + first, first.charAt(0) + last, first + last.charAt(0), last + first.charAt(0),
    ].includes(bare);
}

function emailLocalMatchesFirstName(local: string, name: string): boolean {
    const parts = nameParts(name);
    if (!parts || parts.first.length < 3) return false;
    return local.toLowerCase().replace(/[^a-z]/g, '') === parts.first;
}

// ---------------------------------------------------------------------------
// The deterministic parser
// ---------------------------------------------------------------------------

interface Candidate extends RfqRequester {
    order: number;
}

function labelSpecFor(matchedLabel: string): RequesterLabelSpec | null {
    for (const spec of REQUESTER_LABELS) {
        if (new RegExp(`^${spec.pattern}$`, 'i').test(matchedLabel.trim())) return spec;
    }
    return null;
}

function titleFromSpec(spec: RequesterLabelSpec, matchedLabel: string): string | null {
    if (spec.title === 'printed') return displayTitle(matchedLabel);
    return spec.title;
}

/** What the text knows about the place, for telling a suburb from a surname. */
interface TextContext {
    /** Suburbs printed in an address ("9 Kent Street Randwick NSW 2031"), lowercased. */
    suburbs: Set<string>;
}

const STREET_TYPES = 'street|st|road|rd|avenue|ave|parade|pde|lane|ln|crescent|cres|place|pl|drive|dr|way|close|cl|court|ct|highway|hwy|boulevard|blvd|terrace|tce|circuit|cct';

function textContext(text: string): TextContext {
    const suburbs = new Set<string>();
    const re = new RegExp(
        `\\b(?:${STREET_TYPES})\\.?,?\\s+([A-Za-z][A-Za-z'’-]*(?:\\s+[A-Za-z][A-Za-z'’-]*){0,2})\\s+(?:nsw|vic|qld|sa|wa|tas|nt|act)\\s+\\d{4}\\b`,
        'gi',
    );
    let match: RegExpExecArray | null;
    while ((match = re.exec(text)) !== null) {
        const suburb = match[1] || '';
        if (/^[A-Z]/.test(suburb)) suburbs.add(suburb.toLowerCase());
    }
    return { suburbs };
}

interface NameRun {
    name: string;
    /** The text after the name: a title, the person's own email or phone may follow. */
    rest: string;
}

/**
 * Reads a person's name from the start of a value: one to four capitalised
 * tokens (hyphen and apostrophe allowed), or one "First.Last" login name. The
 * run stops at a digit, a label, a parenthesis, an email, a column gap, a
 * word that is never part of a name, an ALL-CAPS word after a mixed-case
 * name, or a state code. A run that ran into "<Suburb> NSW 2031" gives the
 * suburb back: a suburb the text prints in an address, or else the last word.
 */
function readNameRun(value: string, context: TextContext, { allowSingle = false } = {}): NameRun | null {
    const tokens = value.replace(COLUMN_GAP_RE, ` ${COLUMN_GAP} `).trim().split(/\s+/).filter(Boolean);
    let index = 0;
    while (index < tokens.length && HONORIFICS.has((tokens[index] as string).toLowerCase().replace(/\.$/, ''))) index += 1;
    const first = (tokens[index] || '').replace(/[,;]+$/, '');
    if (!first) return null;
    if (LOGIN_NAME_RE.test(first)) {
        const name = normaliseRequesterName(first);
        return looksLikePersonName(name) ? { name, rest: tokens.slice(index + 1).join(' ') } : null;
    }
    const capsMode = CAPS_NAME_TOKEN_RE.test(first);
    const taken: string[] = [];
    let cursor = index;
    for (; cursor < tokens.length && taken.length < 4; cursor += 1) {
        const raw = tokens[cursor] as string;
        const token = raw.replace(/[,;]+$/, '');
        const bare = token.toLowerCase().replace(/[^a-z]/g, '');
        if (/[\d@:()¦]/.test(token) || !bare || NAME_STOPWORDS.has(bare)) break;
        const fits = capsMode
            ? CAPS_NAME_TOKEN_RE.test(token) || (taken.length > 0 && INITIAL_RE.test(token))
            : MIXED_NAME_TOKEN_RE.test(token)
                || (taken.length > 0 && (INITIAL_RE.test(token) || NAME_PARTICLES.has(token.toLowerCase())));
        if (!fits) break;
        taken.push(token);
        if (raw !== token) {
            cursor += 1;
            break;
        }
    }
    const next = (tokens[cursor] || '').replace(/[,;]+$/, '');
    const ranIntoState = STATE_CODES.has(next.toLowerCase()) && next === next.toUpperCase();
    if (ranIntoState && taken.length >= 2) {
        let gaveBack = false;
        for (let size = Math.min(3, taken.length - 1); size >= 1; size -= 1) {
            if (context.suburbs.has(taken.slice(-size).join(' ').toLowerCase())) {
                taken.splice(-size);
                gaveBack = true;
                break;
            }
        }
        if (!gaveBack && taken.length >= 3) taken.pop();
    }
    const name = normaliseRequesterName(taken.join(' '));
    // A site contact is often printed by first name only ("Emma (U9) 0400 ...").
    const single = allowSingle && taken.length === 1 && name.length >= 2 && MIXED_NAME_TOKEN_RE.test(name);
    if (!single && !looksLikePersonName(name)) return null;
    return { name, rest: tokens.slice(cursor).join(' ') };
}

const PAREN_TITLE_RE = new RegExp(`^\\(\\s*(${TITLE_CORE})\\s*\\)`, 'i');
const TRAILING_TITLE_RE = new RegExp(`^(?:-|–|\\||,)?\\s*(${TITLE_CORE})\\b`, 'i');
// Longest first: the alternation takes the first that matches ("ph" not "p").
const CONTACT_PREFIX_RE = /^(?:[¦,;]\s*)*(?:(?:tel-?[hw]|telephone|tel|phone|ph|p|mobile|mob|m|email|e-mail|e|direct|t)\b\s*[:.]?\s*)?/i;

/**
 * The email and phone printed directly after a person's name, in either
 * order ("Liz Mann 0451 000 000 liz-mann@..."). Anything after another word
 * belongs to the next field. A shared mailbox (office@, repairs@) is never a
 * person's email.
 */
function readImmediateContacts(text: string): { email: string | null; phone: string | null; rest: string } {
    let rest = text;
    let email: string | null = null;
    let phone: string | null = null;
    for (let attempt = 0; attempt < 2; attempt += 1) {
        const tail = rest.trimStart().replace(CONTACT_PREFIX_RE, '');
        const emailHere = emailMatches(tail)[0];
        if (!email && emailHere && emailHere.index === 0) {
            if (!isGenericMailbox(emailHere.email)) email = emailHere.email;
            rest = tail.slice(emailHere.email.length);
            continue;
        }
        const phoneHere = firstPhone(tail);
        if (!phone && phoneHere && phoneHere.index === 0) {
            phone = phoneHere.phone;
            rest = tail.slice(phoneHere.phone.length);
            continue;
        }
        break;
    }
    return { email, phone, rest };
}

/**
 * Parse a label's value: "Jane Citizen (Strata Manager)", "Jane.Citizen",
 * "Jane Citizen - Assistant Strata Manager", "JANE CITIZEN 0400 000 000",
 * "Mia Pollard Randwick NSW 2031". The person's own email or phone counts
 * only when it follows the name directly; anything later belongs to the next
 * field (an access contact's number is not the requester's).
 */
function parseLabelValue(
    segment: string,
    context: TextContext,
): { name: string; title: string | null; email: string | null; phone: string | null } | null {
    const run = readNameRun(segment, context);
    if (!run) return null;
    let rest = run.rest;
    let title: string | null = null;
    const paren = PAREN_TITLE_RE.exec(rest) || TRAILING_TITLE_RE.exec(rest);
    if (paren) {
        title = displayTitle(paren[1] || '');
        rest = rest.slice(paren[0].length).replace(/^\s*\)?/, '');
    }
    const contacts = readImmediateContacts(rest);
    return { name: run.name, title, email: contacts.email, phone: contacts.phone };
}

function contactsOnSegment(segment: string, name: string): { email: string | null; phone: string | null } {
    const emails = emailMatches(segment).map((entry) => entry.email).filter((email) => !isGenericMailbox(email));
    const own = emails.find((email) => emailLocalStronglyMatchesName(splitEmail(email).local, name));
    const phone = firstPhone(segment);
    return { email: own || emails[0] || null, phone: phone ? phone.phone : null };
}

function makeCandidate(
    name: string,
    title: string | null,
    role: RfqRequesterRole,
    label: string,
    order: number,
    email: string | null,
    phone: string | null,
): Candidate {
    return { name, title, email, phone, role: roleFromTitle(title, role), source: { label }, order };
}

function lineIsLabelOnly(line: string): string[] | null {
    const labels: string[] = [];
    const re = new RegExp(ANY_LABEL_GLOBAL_SOURCE, 'gi');
    let match: RegExpExecArray | null;
    while ((match = re.exec(line)) !== null) labels.push(match[0].replace(/\s*:$/, ''));
    if (!labels.length) return null;
    const rest = line.replace(new RegExp(ANY_LABEL_GLOBAL_SOURCE, 'gi'), ' ').trim();
    return rest ? null : labels;
}

// How many non-name lines a pending requester label may pass over before the
// label/value alignment is treated as lost.
const MAX_QUEUE_SKIPS = 6;

function parseLabelledRequesters(
    lines: string[],
    push: (candidate: Candidate) => void,
    context: TextContext,
): boolean {
    let orderByLabelSeen = false;
    let queue: string[] = [];
    let skipped = 0;
    let order = 0;
    for (const line of lines) {
        order += 1;
        if (!line) continue;
        const labelOnly = lineIsLabelOnly(line);
        if (labelOnly) {
            if (labelOnly.some((label) => /^order(?:ed)?\s+by$/i.test(label))) orderByLabelSeen = true;
            queue.push(...labelOnly);
            skipped = 0;
            continue;
        }
        const re = new RegExp(REQUESTER_LABEL_RE.source, 'gi');
        let match: RegExpExecArray | null;
        let inlineFound = false;
        while ((match = re.exec(line)) !== null) {
            inlineFound = true;
            const matchedLabel = match[1] || '';
            const spec = labelSpecFor(matchedLabel);
            if (!spec) continue;
            if (spec.role === 'order_by' && spec.label === 'Order By') orderByLabelSeen = true;
            const rest = line.slice(match.index + match[0].length);
            // An empty value ("Order By: Order Date: ...") is a labels-first
            // layout; the next label starts right here.
            const nextLabel = NEXT_LABEL_RE.exec(` ${rest}`);
            if (nextLabel && !` ${rest}`.slice(0, nextLabel.index).trim()) continue;
            const nextRequester = NEXT_REQUESTER_LABEL_RE.exec(` ${rest}`);
            const segment = nextRequester ? ` ${rest}`.slice(0, nextRequester.index) : rest;
            const parsed = parseLabelValue(segment.trim(), context);
            if (!parsed) continue;
            push(makeCandidate(
                parsed.name,
                parsed.title || titleFromSpec(spec, matchedLabel),
                spec.role,
                spec.label,
                order,
                parsed.email,
                parsed.phone,
            ));
        }
        if (inlineFound) {
            // A requester printed with its value: the layout is not a
            // labels-then-values run here.
            queue = [];
            continue;
        }
        // A self-contained field ("Order No: 98765") answers no pending label.
        if (new RegExp(ANY_LABEL_GLOBAL_SOURCE, 'i').test(line)) continue;
        // A value line under a run of labels printed without their values
        // (pdf text from a two-column form): it answers the oldest label. A
        // requester label waits for a line that reads as a person's name;
        // any other label takes the line as its value.
        const label = queue[0];
        if (!label) continue;
        const spec = labelSpecFor(label);
        if (!spec) {
            queue.shift();
            continue;
        }
        const parsed = parseLabelValue(line, context);
        if (!parsed) {
            skipped += 1;
            if (skipped > MAX_QUEUE_SKIPS) queue = [];
            continue;
        }
        queue.shift();
        skipped = 0;
        push(makeCandidate(
            parsed.name,
            parsed.title || titleFromSpec(spec, label),
            spec.role,
            spec.label,
            order,
            parsed.email,
            parsed.phone,
        ));
    }
    return orderByLabelSeen;
}

const FIRST_DOT_LAST_RE = /(?<![@\w.])([A-Z][a-z]+(?:['’-][A-Za-z]+)?)\.([A-Z][a-z]+(?:['’-][A-Za-z]+)?)(?![\w@.])/g;
const NOT_A_FIRST_NAME: ReadonlySet<string> = new Set(['St', 'Mt', 'Dr', 'Rd', 'No', 'Pty', 'Ltd', 'Co', 'Ave', 'Mr', 'Mrs', 'Ms', 'Pde', 'Hwy', 'Cres', 'Pl']);

/**
 * GK-style forms print the "Order By" value as a login name ("Jane.Citizen"),
 * and the pdf text layer can drop it far from its label. With the label on
 * the page, exactly one such token, and no Order By value found, it is the
 * order's author.
 */
function orderByFromLoginName(text: string, order: number): Candidate | null {
    const names = new Map<string, string>();
    let match: RegExpExecArray | null;
    const re = new RegExp(FIRST_DOT_LAST_RE.source, 'g');
    while ((match = re.exec(text)) !== null) {
        const first = match[1] || '';
        if (NOT_A_FIRST_NAME.has(first)) continue;
        const name = normaliseRequesterName(match[0]);
        if (looksLikePersonName(name)) names.set(requesterNameKey(name), name);
    }
    if (names.size !== 1) return null;
    const name = [...names.values()][0] as string;
    return makeCandidate(name, null, 'order_by', 'Order By', order, null, null);
}

const MONTH_SOURCE = 'jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|june?|july?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?';
const DATE_SOURCE = `(?:\\d{1,2}\\s+(?:${MONTH_SOURCE})\\s+\\d{4}|\\d{1,2}/\\d{1,2}/\\d{2,4})`;
// "Order By: Order Date:": the form printed its labels with no values.
const LABELS_FIRST_ORDER_BY_RE = /\border(?:ed)?\s+by\s*:\s*order\s+date\s*:/i;
// The values of that form, printed after the labels in the form's fixed
// order: ... <suburb> <STATE> <postcode> <Order By> <Order Date> <Quote Due>
// [<No. of Lots>] <Body Corp Manager> ...
const LABELS_FIRST_VALUES_RE = new RegExp(
    `\\b(?:NSW|VIC|QLD|SA|WA|TAS|NT|ACT)\\s+\\d{4}\\s+(\\S+(?:\\s+\\S+){0,3}?)\\s+${DATE_SOURCE}\\s+\\d{1,2}/\\d{1,2}/\\d{2,4}\\s+(?:\\d{1,4}\\s+)?([^\\n]+)`,
    'i',
);
const BODY_CORP_LABEL_RE = /\b(body\s+corp(?:orate)?\.?\s+manager)\s*:/i;

/**
 * GK-style forms whose pdf text prints every label first ("Order By: Order
 * Date: Quote Due: No. of Lots: Body Corp Manager:") and the values after, in
 * the form's fixed order. Anchored on the labels and the value shapes (an
 * address ending in a state and postcode, then the Order By value, two dates,
 * then the manager), never on the file name.
 */
function labelsFirstRequesters(source: string, context: TextContext, push: (candidate: Candidate) => void): void {
    const labels = LABELS_FIRST_ORDER_BY_RE.exec(source);
    if (!labels) return;
    const values = LABELS_FIRST_VALUES_RE.exec(source.slice(labels.index + labels[0].length));
    if (!values) return;
    const orderBy = readNameRun(values[1] || '', context);
    if (orderBy) push(makeCandidate(orderBy.name, null, 'order_by', 'Order By', 0, null, null));
    const managerLabel = BODY_CORP_LABEL_RE.exec(source);
    const manager = managerLabel ? readNameRun(values[2] || '', context) : null;
    if (manager && managerLabel) {
        push(makeCandidate(manager.name, displayTitle(managerLabel[1] || ''), 'strata_manager', 'Body Corp Manager', 0, null, null));
    }
}

function isPersonLine(line: string): string | null {
    if (!line || line.includes(':') || line.length > 60) return null;
    const name = normaliseRequesterName(line.replace(/[,]+$/, ''));
    return looksLikePersonName(name) ? name : null;
}

function parseSignatures(lines: string[], push: (candidate: Candidate) => void): void {
    for (let index = 0; index < lines.length; index += 1) {
        const line = lines[index] || '';
        // "Jane Citizen | Strata Manager | Example Strata"
        const oneLine = NAME_THEN_TITLE_RE.exec(line);
        if (oneLine && !line.includes(':')) {
            const name = normaliseRequesterName(oneLine[1] || '');
            if (looksLikePersonName(name)) {
                const title = displayTitle(oneLine[2] || '');
                const block = lines.slice(index, index + 5).join('  ');
                const contacts = contactsOnSegment(block, name);
                push(makeCandidate(name, title, 'strata_manager', 'Signature', index + 1, contacts.email, contacts.phone));
                continue;
            }
        }
        // "Jane Citizen" / "Strata Manager" / "Example Strata"
        const name = isPersonLine(line);
        if (!name) continue;
        const next = lines[index + 1] || '';
        const titleLine = TITLE_LINE_RE.exec(next);
        if (!titleLine) continue;
        const title = displayTitle(titleLine[1] || '');
        const following: string[] = [];
        for (const candidate of lines.slice(index + 1, index + 7)) {
            if (following.length && isPersonLine(candidate)) break;
            following.push(candidate);
        }
        const contacts = contactsOnSegment(following.join('  '), name);
        push(makeCandidate(name, title, 'strata_manager', 'Signature', index + 1, contacts.email, contacts.phone));
    }
}

/**
 * Order of the list: a manager first (requesters[0] mirrors clientName, the
 * printed Attention line), then the person who ordered or issued the
 * request, then other contacts, then assistants. A request with no manager
 * puts its Order By person first.
 */
function rank(requester: RfqRequester): number {
    const title = requester.title || '';
    const isManager = requester.role === 'strata_manager' || (/manager/i.test(title) && !ASSISTANT_TITLE_RE.test(title));
    if (isManager) return 0;
    if (requester.role === 'issued_by' || requester.role === 'order_by') return 1;
    if (requester.role === 'contact') return 2;
    return 3;
}

function mergeInto(target: RfqRequester, incoming: RfqRequester): RfqRequester {
    const incomingWins = rank(incoming) < rank(target);
    // One person printed as both "Order By" and "Body Corp Manager" stays the
    // Order By person (the quote email's recipient), with the manager title.
    const orderBy = target.role === 'order_by' ? target : incoming.role === 'order_by' ? incoming : null;
    return {
        name: target.name,
        title: target.title || incoming.title,
        email: target.email || incoming.email,
        phone: target.phone || incoming.phone,
        role: orderBy ? 'order_by' : incomingWins ? incoming.role : target.role,
        source: orderBy ? orderBy.source : incomingWins ? incoming.source : target.source,
    };
}

function tenantStaffKeys(context: RequesterTenantContext): Set<string> {
    return new Set((Array.isArray(context.tenantStaffNames) ? context.tenantStaffNames : [])
        .map((name) => requesterNameKey(normaliseRequesterName(name)))
        .filter(Boolean));
}

/**
 * The builder's own people: named in the tenant profile, or matching the local
 * part of a builder-domain email printed in the text (a PICA form's "Contact
 * Name" is the builder's own contact, and the profile may not list them).
 */
function tenantPersonTest(sourceText: string, context: RequesterTenantContext): (name: string) => boolean {
    const staffKeys = tenantStaffKeys(context);
    const tenantEmailLocals = emailMatches(sourceText)
        .map((entry) => entry.email)
        .filter((email) => isTenantEmail(email, context))
        .map((email) => splitEmail(email).local);
    return (name: string) => staffKeys.has(requesterNameKey(name))
        || tenantEmailLocals.some((local) => emailLocalStronglyMatchesName(local, name));
}

function docName(doc: unknown): string | undefined {
    const value = cleanText(doc);
    if (!value) return undefined;
    // Keep only the file name: a path or folder name is not provenance we need.
    return value.split(/[\\/]/).pop() || undefined;
}

/**
 * Every person who requested or manages the request, primary first: strata
 * managers, then the person who ordered or issued it, then other contacts,
 * then assistants; ties keep document order. The builder's own staff and
 * domains are excluded.
 */
export function extractRfqRequesters(text: unknown, options: ExtractRfqRequestersOptions = {}): RfqRequester[] {
    const source = typeof text === 'string' ? text : '';
    if (!source.trim()) return [];
    const lines = source.split(/\r?\n/).map((line) => line.trim());
    const isTenantPerson = tenantPersonTest(source, options);
    const allEmails = emailMatches(source)
        .map((entry) => entry.email)
        .filter((email) => !isTenantEmail(email, options));

    const candidates: Candidate[] = [];
    const push = (candidate: Candidate) => { candidates.push(candidate); };
    const context = textContext(source);
    const orderByLabelSeen = parseLabelledRequesters(lines, push, context);
    labelsFirstRequesters(source, context, push);
    if (orderByLabelSeen && !candidates.some((candidate) => candidate.source.label === 'Order By')) {
        const loginName = orderByFromLoginName(source, 0);
        if (loginName) push(loginName);
    }
    parseSignatures(lines, push);

    // A site contact is never a requester (Peter, 2026-10-13). A signature is
    // only a guess about who asked; a person printed under a requester label
    // ("Body Corp Manager: X") stays a requester even when they are also the
    // one to call for access.
    const siteKeys = new Set(extractRfqSiteContacts(source, options).map((contact) => requesterNameKey(contact.name)));
    const merged = new Map<string, Candidate>();
    for (const candidate of candidates) {
        if (isTenantPerson(candidate.name)) continue;
        if (candidate.source.label === 'Signature' && siteKeys.has(requesterNameKey(candidate.name))) continue;
        const key = requesterNameKey(candidate.name);
        const existing = merged.get(key);
        merged.set(key, existing
            ? { ...mergeInto(existing, candidate), order: Math.min(existing.order, candidate.order) }
            : candidate);
    }

    const people = [...merged.values()];
    for (const person of people) {
        if (person.email && (isTenantEmail(person.email, options) || isGenericMailbox(person.email))) person.email = null;
    }
    // An email printed anywhere whose local part spells the person's name is
    // theirs ("jane.citizen@..."). A first-name-only mailbox counts only when
    // one person on the request has that first name.
    for (const person of people) {
        if (person.email) continue;
        const own = allEmails.find((email) => !isGenericMailbox(email)
            && emailLocalStronglyMatchesName(splitEmail(email).local, person.name));
        const firstOnly = own ? null : allEmails.filter((email) => !isGenericMailbox(email)
            && emailLocalMatchesFirstName(splitEmail(email).local, person.name));
        const sharesFirstName = people.filter((other) => nameParts(other.name)?.first === nameParts(person.name)?.first).length > 1;
        person.email = own || (firstOnly && firstOnly.length === 1 && !sharesFirstName ? firstOnly[0] as string : null);
    }

    const doc = docName(options.doc);
    return people
        .map((person) => ({ ...person, email: person.email ? person.email.toLowerCase() : null }))
        .sort((left, right) => (rank(left) - rank(right)) || (left.order - right.order))
        .map(({ order: _order, ...person }) => ({
            name: person.name,
            title: person.title,
            email: person.email,
            phone: person.phone,
            role: person.role,
            source: doc ? { doc, label: person.source.label } : { label: person.source.label },
        }));
}

// ---------------------------------------------------------------------------
// Site contacts (Peter, 2026-10-13): "Quote Requests (RFQs) usually have the
// Name and Contact details of a Site Contact. These are either body corp
// managers, or tenants, or owners of property, and these are NOT the
// requester. The business is just expected to contact the Site Contact(s) to
// schedule a site visit and availability."
// ---------------------------------------------------------------------------

export type RfqSiteContactRelation = 'tenant' | 'owner' | 'building_manager' | 'unknown';

export interface RfqSiteContact {
    name: string;
    phone: string | null;
    email: string | null;
    role: 'site_contact';
    relation: RfqSiteContactRelation;
    source: RfqRequesterSource;
}

interface SiteLabelSpec {
    pattern: string;
    label: string;
    relation: RfqSiteContactRelation;
    /** One-word labels ("Owner") count only with a colon, so prose does not. */
    colon: boolean;
}

const SITE_CONTACT_LABELS: readonly SiteLabelSpec[] = [
    { pattern: 'site\\s+contact\\s+person', label: 'Site Contact Person', relation: 'unknown', colon: false },
    { pattern: 'site\\s+contact(?:\\(s\\)|s)?', label: 'Site Contact', relation: 'unknown', colon: false },
    { pattern: 'access\\s+contact', label: 'Access Contact', relation: 'unknown', colon: false },
    { pattern: 'contact\\s+on\\s+site', label: 'Contact on site', relation: 'unknown', colon: false },
    { pattern: 'on[-\\s]?site\\s+contact', label: 'Onsite contact', relation: 'unknown', colon: false },
    { pattern: 'lot\\s+owner', label: 'Lot owner', relation: 'owner', colon: true },
    { pattern: 'owner', label: 'Owner', relation: 'owner', colon: true },
    { pattern: 'tenant', label: 'Tenant', relation: 'tenant', colon: true },
    { pattern: 'occupant', label: 'Occupant', relation: 'tenant', colon: true },
    { pattern: 'resident', label: 'Resident', relation: 'tenant', colon: true },
    { pattern: 'building\\s+manager', label: 'Building Manager', relation: 'building_manager', colon: true },
    { pattern: 'caretaker', label: 'Caretaker', relation: 'building_manager', colon: true },
];
const siteLabelSource = (spec: SiteLabelSpec) => `${spec.pattern}${spec.colon ? '\\s*:' : '\\s*:?'}`;
const SITE_LABEL_SOURCE = `\\b(?:${SITE_CONTACT_LABELS.map(siteLabelSource).join('|')})`;
// Where a site-contact section ends: another requester or site-contact
// label, or the next part of the form.
const SITE_SECTION_END_RE = new RegExp(
    `\\s(?:\\b(?:${REQUESTER_LABELS.map((spec) => spec.pattern).join('|')})\\s*:|${SITE_LABEL_SOURCE}`
    + '|\\b(?:job\\s+details|job\\s+description|property\\s+affected|special\\s+instructions|attention\\s+to|contractor(?:\\s+name)?|bill\\s+to|quote\\s+due)\\b)',
    'i',
);

function relationFrom(text: string, fallback: RfqSiteContactRelation): RfqSiteContactRelation {
    if (/\b(?:building|resident|facilities)\s+manager\b|\bcaretaker\b|\bbody\s+corp(?:orate)?\.?\s+manager\b|\bstrata\s+manager\b/i.test(text)) return 'building_manager';
    if (/\bowner\b/i.test(text)) return 'owner';
    if (/\b(?:tenant|occupant|resident)\b/i.test(text)) return 'tenant';
    return fallback;
}

function siteSpecFor(matched: string): SiteLabelSpec | null {
    const bare = matched.replace(/\s*:$/, '').trim();
    for (const spec of SITE_CONTACT_LABELS) {
        if (new RegExp(`^(?:${spec.pattern})$`, 'i').test(bare)) return spec;
    }
    return null;
}

interface SiteCandidate extends RfqSiteContact {
    order: number;
}

/**
 * Reads the people in one site-contact section: "Name Ms Sally M Kerr Tel-H:
 * 02 ...", "Liz Mann 0451 ... liz@...", "Emma (U9) 0400 ..., Ruby Lane (U4)
 * 0400 ...". A first name alone counts only with a phone or a unit beside it.
 */
function readSiteSection(
    window: string,
    relation: RfqSiteContactRelation,
    label: string,
    order: number,
    context: TextContext,
    push: (contact: SiteCandidate) => void,
): void {
    let rest = window;
    for (let count = 0; count < 4; count += 1) {
        rest = rest.replace(/^\s*(?:(?:name|person)\b\s*:?\s*|\(s\)\s*:?\s*)+/i, '').replace(/^[\s,;&¦]+(?:and\s+)?/i, '');
        const run = readNameRun(rest, context, { allowSingle: true });
        if (!run) return;
        let tail = run.rest;
        let personRelation = relation;
        const paren = /^\(([^)]{1,40})\)\s*/.exec(tail);
        if (paren) {
            personRelation = relationFrom(paren[1] || '', relation);
            tail = tail.slice(paren[0].length);
        }
        const contacts = readImmediateContacts(tail);
        const single = !run.name.includes(' ');
        if (single && !contacts.phone && !paren) return;
        push({
            name: run.name,
            phone: contacts.phone,
            email: contacts.email,
            role: 'site_contact',
            relation: personRelation,
            source: { label },
            order,
        });
        rest = contacts.rest;
        if (!/^\s*(?:[,;&]|and\b)/i.test(rest)) return;
    }
}

const ARRANGE_ACCESS_RE = /\b(?:to|for)\s+(?:arrange|organise|organize|book|schedule|gain|coordinate|confirm|obtain)\b[^.\n]{0,30}?\b(?:access|entry|times?|inspection|visit|attendance|appointment)\b/i;
const CONTACT_VERB_RE = /\b(?:please\s+)?(?:contact|call|phone|ring|liaise\s+with)\s+/gi;
const ACCESS_RELATION_RE = /^(?:the\s+)?(lot\s+owner|owner|tenant|occupant|resident|building\s+manager|caretaker|body\s+corp(?:orate)?\.?\s+manager|strata\s+manager)\b\s*,?\s*/i;

/**
 * The people to call to arrange the site visit: site-contact, access,
 * tenant, owner, occupant and building-manager fields, and "please contact
 * <name> to arrange access" in an email. Read from the text only; the
 * builder's own staff are left out; an email counts only when printed.
 */
export function extractRfqSiteContacts(text: unknown, options: ExtractRfqRequestersOptions = {}): RfqSiteContact[] {
    const source = typeof text === 'string' ? text : '';
    if (!source.trim()) return [];
    const context = textContext(source);
    const isTenantPerson = tenantPersonTest(source, options);
    const candidates: SiteCandidate[] = [];
    const push = (contact: SiteCandidate) => { candidates.push(contact); };

    source.split(/\r?\n/).forEach((line, lineIndex) => {
        const labelRe = new RegExp(SITE_LABEL_SOURCE, 'gi');
        let match: RegExpExecArray | null;
        while ((match = labelRe.exec(line)) !== null) {
            const spec = siteSpecFor(match[0]);
            if (!spec) continue;
            const after = line.slice(match.index + match[0].length).replace(/^\s*:/, '');
            const end = SITE_SECTION_END_RE.exec(` ${after}`);
            const window = (end ? after.slice(0, Math.max(0, end.index - 1)) : after).slice(0, 240);
            readSiteSection(window, spec.relation, spec.label, lineIndex + 1, context, push);
        }
    });

    const verbRe = new RegExp(CONTACT_VERB_RE.source, 'gi');
    let verb: RegExpExecArray | null;
    while ((verb = verbRe.exec(source)) !== null) {
        const window = source.slice(verb.index + verb[0].length, verb.index + verb[0].length + 180);
        const arrange = ARRANGE_ACCESS_RE.exec(window);
        if (!arrange) continue;
        let segment = window.slice(0, arrange.index);
        let relation: RfqSiteContactRelation = 'unknown';
        const relationWord = ACCESS_RELATION_RE.exec(segment);
        if (relationWord) {
            relation = relationFrom(relationWord[1] || '', 'unknown');
            segment = segment.slice(relationWord[0].length);
        }
        readSiteSection(segment, relation, 'Arrange access', 0, context, push);
    }

    const allEmails = emailMatches(source).map((entry) => entry.email);
    const merged = new Map<string, SiteCandidate>();
    for (const candidate of candidates) {
        if (isTenantPerson(candidate.name)) continue;
        const key = requesterNameKey(candidate.name);
        const existing = merged.get(key);
        merged.set(key, existing ? {
            ...existing,
            phone: existing.phone || candidate.phone,
            email: existing.email || candidate.email,
            relation: existing.relation === 'unknown' ? candidate.relation : existing.relation,
            order: Math.min(existing.order, candidate.order),
        } : candidate);
    }
    const doc = docName(options.doc);
    return [...merged.values()]
        .map((contact) => {
            let email = contact.email && !isTenantEmail(contact.email, options) ? contact.email : null;
            if (!email && contact.name.includes(' ')) {
                email = allEmails.find((candidate) => !isGenericMailbox(candidate) && !isTenantEmail(candidate, options)
                    && emailLocalStronglyMatchesName(splitEmail(candidate).local, contact.name)) || null;
            }
            return { ...contact, email: email ? email.toLowerCase() : null };
        })
        .sort((left, right) => left.order - right.order)
        .map(({ order: _order, ...contact }) => ({
            name: contact.name,
            phone: contact.phone,
            email: contact.email,
            role: 'site_contact' as const,
            relation: contact.relation,
            source: doc ? { doc, label: contact.source.label } : { label: contact.source.label },
        }));
}

/** Validates a stored or AI-shaped site-contact list. */
export function sanitizeSiteContacts(value: unknown): RfqSiteContact[] {
    if (!Array.isArray(value)) return [];
    const out: RfqSiteContact[] = [];
    const seen = new Set<string>();
    for (const entry of value) {
        if (!entry || typeof entry !== 'object') continue;
        const record = entry as Record<string, unknown>;
        const name = normaliseRequesterName(record.name);
        const key = requesterNameKey(name);
        if (!key || name.length > 80 || name.includes('@') || seen.has(key)) continue;
        seen.add(key);
        const rawSource = record.source && typeof record.source === 'object' ? record.source as Record<string, unknown> : {};
        const doc = docName(rawSource.doc);
        const relation = ['tenant', 'owner', 'building_manager'].includes(String(record.relation))
            ? record.relation as RfqSiteContactRelation
            : 'unknown';
        const email = cleanText(record.email).slice(0, 200);
        const phone = cleanText(record.phone).slice(0, 40);
        const label = cleanText(rawSource.label).slice(0, 80) || 'Unknown';
        out.push({
            name,
            phone: phone || null,
            email: email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email.toLowerCase() : null,
            role: 'site_contact',
            relation,
            source: doc ? { doc, label } : { label },
        });
    }
    return out;
}

/**
 * The site-contact list to write, or null when nothing changes. Contacts
 * already on the project keep their place and values; new ones are added.
 */
export function applyExtractedSiteContacts(
    formData: Record<string, unknown> | null | undefined,
    incoming: unknown,
): RfqSiteContact[] | null {
    const additions = sanitizeSiteContacts(incoming);
    if (!additions.length) return null;
    const next = sanitizeSiteContacts(formData?.siteContacts);
    for (const contact of additions) {
        const index = next.findIndex((existing) => sameRequesterName(existing.name, contact.name));
        if (index < 0) {
            next.push(contact);
            continue;
        }
        const current = next[index] as RfqSiteContact;
        next[index] = {
            ...current,
            phone: current.phone || contact.phone,
            email: current.email || contact.email,
            relation: current.relation === 'unknown' ? contact.relation : current.relation,
        };
    }
    const before = Array.isArray(formData?.siteContacts) ? formData?.siteContacts : [];
    return JSON.stringify(next) === JSON.stringify(before) ? null : next;
}

// ---------------------------------------------------------------------------
// Where the firm wants quotes sent (Peter, 2026-10-13: the quote is emailed
// to "the Firm's email address where quotes are requested to be sent to"
// plus the Order By person)
// ---------------------------------------------------------------------------

const QUOTE_WORDS = '(?:quotes?|quotations?|tenders?)';
const QUOTE_RETURN_SOURCES: readonly string[] = [
    // "Please email your quotation to quotes@...", "forward quotes to ...",
    // "submit your quote quoting the order number to ..."
    `\\b(?:send|email|e-mail|submit|forward|return|direct|lodge|address|provide)\\b[^@\\n]{0,80}?\\b${QUOTE_WORDS}\\b[^@\\n]{0,60}?\\b(?:to|at|via)\\s*:?\\s*(${EMAIL_SOURCE})`,
    // "Quotes to: office@...", "Quotations should be sent to ..."
    `\\b${QUOTE_WORDS}\\s+(?:(?:should|must|can|are\\s+to)\\s+be\\s+)?(?:sent|emailed|submitted|forwarded|returned|directed|addressed)?\\s*(?:to|at)\\s*:?\\s*(${EMAIL_SOURCE})`,
    // "Quotations: tenders@...", "Quotes Email: ..."
    `\\b${QUOTE_WORDS}(?:\\s+e-?mail)?\\s*:\\s*(${EMAIL_SOURCE})`,
];

/**
 * The address the request asks quotes to be sent to, when the text prints
 * one beside that instruction. A shared mailbox (quotes@, office@) is right
 * here: it is the firm's address, not a person's. Never the builder's own.
 */
export function extractQuoteReturnEmail(text: unknown, context: RequesterTenantContext = {}): string | null {
    const source = typeof text === 'string' ? text : '';
    if (!source) return null;
    const found: Array<{ index: number; email: string }> = [];
    for (const pattern of QUOTE_RETURN_SOURCES) {
        const re = new RegExp(pattern, 'gi');
        let match: RegExpExecArray | null;
        while ((match = re.exec(source)) !== null) {
            found.push({ index: match.index, email: (match[1] || '').replace(/[.]+$/, '') });
        }
    }
    found.sort((left, right) => left.index - right.index);
    for (const { email } of found) {
        const accepted = acceptExtractedEmail(email, source, context);
        if (accepted) return accepted;
    }
    return null;
}

// ---------------------------------------------------------------------------
// Persisted requesters (formData.requesters)
// ---------------------------------------------------------------------------

function stringOrNull(value: unknown, max = 200): string | null {
    const text = cleanText(value);
    return text ? text.slice(0, max) : null;
}

/** Validates a stored or AI-shaped requester list into the RfqRequester shape. */
export function sanitizeRequesters(value: unknown): RfqRequester[] {
    if (!Array.isArray(value)) return [];
    const out: RfqRequester[] = [];
    const seen = new Set<string>();
    for (const entry of value) {
        if (!entry || typeof entry !== 'object') continue;
        const record = entry as Record<string, unknown>;
        const name = normaliseRequesterName(record.name);
        const key = requesterNameKey(name);
        if (!key || name.length > 80 || name.includes('@') || seen.has(key)) continue;
        seen.add(key);
        const rawSource = record.source && typeof record.source === 'object'
            ? record.source as Record<string, unknown>
            : {};
        const doc = docName(rawSource.doc);
        const role = typeof record.role === 'string' && ROLES.has(record.role)
            ? record.role as RfqRequesterRole
            : 'contact';
        const email = stringOrNull(record.email);
        out.push({
            name,
            title: stringOrNull(record.title, 80),
            email: email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email.toLowerCase() : null,
            phone: stringOrNull(record.phone, 40),
            role,
            source: doc
                ? { doc, label: stringOrNull(rawSource.label, 80) || 'Unknown' }
                : { label: stringOrNull(rawSource.label, 80) || 'Unknown' },
        });
    }
    return out;
}

/**
 * Union by name. `first` keeps its order and its values; `second` fills gaps
 * and adds people `first` does not have.
 */
export function mergeRequesterLists(first: readonly RfqRequester[], second: readonly RfqRequester[]): RfqRequester[] {
    const out: RfqRequester[] = first.map((requester) => ({ ...requester, source: { ...requester.source } }));
    for (const incoming of second) {
        const index = out.findIndex((requester) => sameRequesterName(requester.name, incoming.name));
        if (index < 0) {
            out.push({ ...incoming, source: { ...incoming.source } });
            continue;
        }
        const current = out[index] as RfqRequester;
        out[index] = {
            ...current,
            title: current.title || incoming.title,
            email: current.email || incoming.email,
            phone: current.phone || incoming.phone,
        };
    }
    return out;
}

/**
 * Keeps requesters[0] equal to the single client fields Stage 1 shows
 * (clientName / clientEmail / clientTitle / clientPhone), which the
 * letterhead, greeting and addressee read. The client named in those fields
 * moves to the front, taking their values; a client not in the list is added
 * first.
 */
export function reconcileRequestersWithClient(
    formData: Record<string, unknown> | null | undefined,
    requesters: readonly RfqRequester[],
): RfqRequester[] {
    const list = requesters.map((requester) => ({ ...requester, source: { ...requester.source } }));
    const clientName = normaliseRequesterName(formData?.clientName);
    if (!requesterNameKey(clientName)) return list;
    const clientEmail = stringOrNull(formData?.clientEmail);
    const clientTitle = stringOrNull(formData?.clientTitle, 80);
    const clientPhone = stringOrNull(formData?.clientPhone, 40);
    const index = list.findIndex((requester) => sameRequesterName(requester.name, clientName));
    if (index >= 0) {
        const current = list[index] as RfqRequester;
        const primary: RfqRequester = {
            ...current,
            email: clientEmail ? clientEmail.toLowerCase() : current.email,
            title: clientTitle || current.title,
            phone: clientPhone || current.phone,
        };
        return [primary, ...list.slice(0, index), ...list.slice(index + 1)];
    }
    return [{
        name: clientName,
        title: clientTitle,
        email: clientEmail ? clientEmail.toLowerCase() : null,
        phone: clientPhone,
        role: roleFromTitle(clientTitle, 'contact'),
        source: { label: 'Quote requester' },
    }, ...list];
}

/** The requesters of a project, primary first and aligned with its client fields. */
export function requestersOf(formData: Record<string, unknown> | null | undefined): RfqRequester[] {
    return reconcileRequestersWithClient(formData, sanitizeRequesters(formData?.requesters));
}

/**
 * The requesters list to write after an extraction or a handoff, or null when
 * nothing changes. People already on the project keep their place and values;
 * new people are added after them; requesters[0] follows the client fields.
 */
export function applyExtractedRequesters(
    formData: Record<string, unknown> | null | undefined,
    incoming: unknown,
): RfqRequester[] | null {
    const additions = sanitizeRequesters(incoming);
    if (!additions.length) return null;
    const existing = sanitizeRequesters(formData?.requesters);
    const next = reconcileRequestersWithClient(formData, mergeRequesterLists(existing, additions));
    const before = Array.isArray(formData?.requesters) ? formData?.requesters : [];
    return JSON.stringify(next) === JSON.stringify(before) ? null : next;
}

/** Every active strata-manager contact of an intake job, primary first. */
export function requestersFromIntakeContacts(contacts: unknown): RfqRequester[] {
    if (!Array.isArray(contacts)) return [];
    const field = (record: Record<string, unknown>, ...keys: string[]): unknown => {
        for (const key of keys) if (record[key] != null) return record[key];
        return undefined;
    };
    const managers = contacts
        .filter((contact): contact is Record<string, unknown> => !!contact && typeof contact === 'object')
        .filter((contact) => contact.role === 'strata_manager'
            && field(contact, 'is_active', 'isActive') !== false);
    const primary = managers.filter((contact) => field(contact, 'is_primary', 'isPrimary') === true);
    const others = managers.filter((contact) => field(contact, 'is_primary', 'isPrimary') !== true);
    return sanitizeRequesters([...primary, ...others].map((contact) => ({
        name: field(contact, 'full_name', 'fullName', 'name'),
        title: null,
        email: field(contact, 'email'),
        phone: field(contact, 'phone'),
        role: 'strata_manager',
        source: { label: 'Intake contact' },
    })));
}

/**
 * Every active site contact of an intake job, then the building manager, each
 * primary first, in Stage 1's site-contact shape. Site contacts are never
 * requesters (Peter, 2026-10-13), so this list never feeds `requesters`.
 */
export function siteContactsFromIntakeContacts(contacts: unknown): RfqSiteContact[] {
    return sanitizeSiteContacts(onSiteIntakePeople(contacts).map((person) => ({
        name: person.name,
        phone: person.phone,
        email: person.email,
        relation: person.role === 'building_manager' ? 'building_manager' : 'unknown',
        source: { label: 'Intake contact' },
    })));
}

interface OnSiteIntakePerson { name: string; phone: string; email: string; role: string }

/** The active on-site people of an intake job in hand-over order, named or not. */
function onSiteIntakePeople(contacts: unknown): OnSiteIntakePerson[] {
    if (!Array.isArray(contacts)) return [];
    const field = (record: Record<string, unknown>, ...keys: string[]): unknown => {
        for (const key of keys) if (record[key] != null) return record[key];
        return undefined;
    };
    const active = contacts
        .filter((contact): contact is Record<string, unknown> => !!contact && typeof contact === 'object')
        .filter((contact) => field(contact, 'is_active', 'isActive') !== false);
    const ofRole = (role: string) => {
        const people = active.filter((contact) => contact.role === role);
        return [
            ...people.filter((contact) => field(contact, 'is_primary', 'isPrimary') === true),
            ...people.filter((contact) => field(contact, 'is_primary', 'isPrimary') !== true),
        ];
    };
    return [...ofRole('site_contact'), ...ofRole('building_manager')]
        .map((contact) => ({
            name: cleanText(field(contact, 'full_name', 'fullName', 'name')),
            phone: cleanText(field(contact, 'phone')),
            email: cleanText(field(contact, 'email')),
            role: String(contact.role),
        }))
        .filter((person) => person.name || person.phone || person.email);
}

/**
 * The person Stage 1's single site-contact fields take: the first on site,
 * even one saved with only a phone (whom the site-contact list cannot hold,
 * because a list entry needs a name).
 */
export function mainSiteContactFromIntakeContacts(contacts: unknown): { name: string; phone: string; email: string } | null {
    const first = onSiteIntakePeople(contacts)[0];
    if (!first) return null;
    // The same address rule as a list entry (sanitizeSiteContacts).
    const email = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(first.email) ? first.email.toLowerCase() : '';
    return { name: normaliseRequesterName(first.name), phone: first.phone.slice(0, 40), email };
}

/**
 * The people half of the snapshot an intake job hands Stage 1 when its quote
 * opens, from the job's contacts. One builder for every way in (the docket,
 * the Register, the Assistant), so each gives Stage 1 the same people. The
 * client is an active strata manager, primary first, never a site contact.
 */
export function intakePeopleSnapshot(contacts: unknown): {
    strata_manager_name: string | null; strata_manager_email: string | null;
    strata_manager_phone: string | null; strata_company_name: string | null;
    requesters: RfqRequester[]; site_contacts: RfqSiteContact[];
    site_contact: { name: string; phone: string; email: string } | null;
} {
    const list = Array.isArray(contacts) ? contacts.filter((contact): contact is Record<string, unknown> => !!contact && typeof contact === 'object') : [];
    const value = (record: Record<string, unknown>, ...keys: string[]): string | null => {
        for (const key of keys) {
            const raw = record[key];
            if (typeof raw === 'string' && raw.trim()) return raw;
        }
        return null;
    };
    const managers = list.filter((contact) => contact.role === 'strata_manager'
        && (contact.is_active ?? contact.isActive) !== false);
    const client = managers.find((contact) => (contact.is_primary ?? contact.isPrimary) === true) || managers[0] || {};
    return {
        strata_manager_name: value(client, 'full_name', 'fullName', 'name'),
        strata_manager_email: value(client, 'email'),
        strata_manager_phone: value(client, 'phone'),
        strata_company_name: value(client, 'company_name', 'companyName', 'company'),
        // Every active strata manager, primary first, so Stage 1 knows each
        // requester on the job, not only one.
        requesters: requestersFromIntakeContacts(list),
        // Everyone the assessor meets, kept apart from the requesters, and
        // the first of them (who may have only a phone) for the single fields.
        site_contacts: siteContactsFromIntakeContacts(list),
        site_contact: mainSiteContactFromIntakeContacts(list),
    };
}

/**
 * The Stage 1 fields to add when a promoted intake job opens: the site
 * contacts list and the single site-contact fields, or null to leave the
 * project as it is. Only a project with no site contact at all is seeded, so
 * a typed name never pairs with another person's phone, and a person removed
 * in Stage 1 does not come back while anyone is left (clearing every site
 * contact lets the intake job's people back in on the next open).
 */
export function seedSiteContactsFromIntake(
    formData: Record<string, unknown> | null | undefined,
    snapshot: { site_contacts?: unknown; site_contact?: unknown } | null | undefined,
): Record<string, unknown> | null {
    const current = formData || {};
    const hasSiteContact = (Array.isArray(current.siteContacts) && current.siteContacts.length > 0)
        || ['siteContactName', 'siteContactPhone', 'siteContactEmail'].some((key) => cleanText(current[key]));
    if (hasSiteContact) return null;
    const list = sanitizeSiteContacts(snapshot?.site_contacts);
    const main = snapshot?.site_contact && typeof snapshot.site_contact === 'object'
        ? snapshot.site_contact as Record<string, unknown>
        : list[0] || null;
    if (!main) return null;
    const name = cleanText(main.name);
    const phone = cleanText(main.phone);
    const email = cleanText(main.email);
    if (!name && !phone && !email) return null;
    return {
        ...(list.length > 0 ? { siteContacts: list } : {}),
        siteContactName: name,
        siteContactPhone: phone,
        siteContactEmail: email,
    };
}

// ---------------------------------------------------------------------------
// Merging the AI extraction (the fallback) with the deterministic parser
// ---------------------------------------------------------------------------

export interface ContactExtractionGuardContext extends RequesterTenantContext {
    /** The exact text the extraction read. */
    sourceText: string;
    /** File name. Provenance only. */
    doc?: string | null;
}

function aiRequesterFrom(
    record: Record<string, unknown>,
    context: ContactExtractionGuardContext,
    isTenantPerson: (name: string) => boolean,
    label: string,
): RfqRequester | null {
    const name = normaliseRequesterName(record.name);
    if (!requesterNameKey(name) || name.length > 80 || /[@0-9]/.test(name)) return null;
    if (isTenantPerson(name)) return null;
    const title = stringOrNull(record.title, 80);
    const rawRole = typeof record.role === 'string' ? record.role : '';
    const role = ROLES.has(rawRole) ? rawRole as RfqRequesterRole : roleFromTitle(title, 'contact');
    const phone = stringOrNull(record.phone, 40);
    return {
        name,
        title,
        email: acceptExtractedEmail(record.email, context.sourceText, context),
        phone: phone && phone.replace(/\D/g, '').length >= 8 ? phone : null,
        role: roleFromTitle(title, role),
        source: { label },
    };
}

/**
 * The one finishing step for a contact extraction (Stage 1, the queue, the
 * Assistant re-extract). It
 *  1. drops a clientEmail that is not printed in the source or is the
 *     builder's own, and a clientName that is the builder's own staff;
 *  2. lists the site contacts (parser first, then the AI's siteContactName)
 *     and keeps them out of the requesters, clientName, clientEmail and
 *     quoteReturnEmail;
 *  3. lists the requesters: the deterministic parser first, then the AI's;
 *  4. sets the client fields to requesters[0], so the letterhead, greeting
 *     and addressee keep reading the primary requester;
 *  5. reads quoteReturnEmail, the address the request asks quotes be sent to.
 * Returns null only when the AI returned nothing and the parser found nothing.
 */
export function finaliseContactExtraction(
    extracted: Record<string, any> | null | undefined,
    context: ContactExtractionGuardContext,
): Record<string, any> | null {
    const sourceText = typeof context.sourceText === 'string' ? context.sourceText : '';
    const isTenantPerson = tenantPersonTest(sourceText, context);
    const result: Record<string, any> = extracted && typeof extracted === 'object' ? { ...extracted } : {};
    const doc = docName(context.doc);

    const rawEmail = cleanText(result.clientEmail);
    let guardedEmail = rawEmail ? acceptExtractedEmail(rawEmail, sourceText, context) : null;
    const aiClientName = normaliseRequesterName(result.clientName);
    if (aiClientName && isTenantPerson(aiClientName)) result.clientName = null;

    const deterministic = extractRfqRequesters(sourceText, { ...context, doc: context.doc });

    // Site contacts: the parser's, then the AI's single site contact.
    const sites: RfqSiteContact[] = extractRfqSiteContacts(sourceText, { ...context, doc: context.doc });
    const aiSiteName = normaliseRequesterName(result.siteContactName);
    if (requesterNameKey(aiSiteName) && !isTenantPerson(aiSiteName)
        && !sites.some((site) => sameRequesterName(site.name, aiSiteName))) {
        const aiSitePhone = stringOrNull(result.siteContactPhone, 40);
        sites.push({
            name: aiSiteName,
            phone: aiSitePhone && aiSitePhone.replace(/\D/g, '').length >= 8 ? aiSitePhone : null,
            email: null,
            role: 'site_contact',
            relation: 'unknown',
            source: doc ? { doc, label: 'AI extraction' } : { label: 'AI extraction' },
        });
    }
    const labelledKeys = new Set(deterministic.map((requester) => requesterNameKey(requester.name)));
    // A person printed under a requester label stays a requester even when
    // they are also the one to call for access.
    const isSiteOnly = (name: string) => {
        const key = requesterNameKey(name);
        return !labelledKeys.has(key) && sites.some((site) => requesterNameKey(site.name) === key);
    };
    const siteEmailOwner = new Map(sites.filter((site) => site.email).map((site) => [site.email as string, site.name]));
    const isOtherSiteEmail = (email: string | null, name: string) => {
        const owner = email ? siteEmailOwner.get(email) : undefined;
        return !!owner && !sameRequesterName(owner, name);
    };
    if (requesterNameKey(normaliseRequesterName(result.clientName)) && isSiteOnly(normaliseRequesterName(result.clientName))) {
        result.clientName = null;
    }
    if (guardedEmail && siteEmailOwner.has(guardedEmail) && !labelledKeys.has(requesterNameKey(siteEmailOwner.get(guardedEmail)))) {
        guardedEmail = null;
    }
    if (rawEmail && !guardedEmail) result.clientEmail = null;
    else if (guardedEmail) result.clientEmail = guardedEmail;

    if (sites.length) {
        const first = sites[0] as RfqSiteContact;
        result.siteContacts = sites;
        result.siteContactPhone = first.phone
            || (sameRequesterName(aiSiteName, first.name) ? stringOrNull(result.siteContactPhone, 40) : null);
        result.siteContactName = first.name;
    } else {
        delete result.siteContacts;
    }

    // Where the firm wants the quote sent: printed beside the instruction,
    // never the builder's own address and never a site contact's.
    const quoteReturn = extractQuoteReturnEmail(sourceText, context)
        || acceptExtractedEmail(result.quoteReturnEmail, sourceText, context);
    if (quoteReturn && !siteEmailOwner.has(quoteReturn)) result.quoteReturnEmail = quoteReturn;
    else delete result.quoteReturnEmail;

    const aiPeople: RfqRequester[] = [];
    const clientName = normaliseRequesterName(result.clientName);
    if (requesterNameKey(clientName)) {
        const client = aiRequesterFrom({
            name: clientName,
            title: result.clientTitle,
            email: result.clientEmail,
            phone: result.clientPhone,
        }, context, isTenantPerson, 'AI extraction');
        if (client) aiPeople.push(client);
    }
    if (Array.isArray(result.requesters)) {
        for (const entry of result.requesters) {
            if (!entry || typeof entry !== 'object') continue;
            const person = aiRequesterFrom(entry as Record<string, unknown>, context, isTenantPerson, 'AI extraction');
            if (person && !isSiteOnly(person.name)) aiPeople.push(person);
        }
    }
    const withDoc = (list: RfqRequester[]) => list.map((requester) => ({
        ...requester,
        email: isOtherSiteEmail(requester.email, requester.name) ? null : requester.email,
        source: doc ? { doc, label: requester.source.label } : { label: requester.source.label },
    }));
    const requesters = withDoc(mergeRequesterLists(deterministic, mergeRequesterLists([], aiPeople)));

    if (!requesters.length) {
        delete result.requesters;
        const foundSomething = !!(extracted && typeof extracted === 'object') || sites.length > 0 || !!result.quoteReturnEmail;
        return foundSomething ? result : null;
    }

    const primary = requesters[0] as RfqRequester;
    // The AI's single client fields may fill the primary's gaps only when the
    // AI named no one or named this same person.
    const aiSpeaksForPrimary = !requesterNameKey(clientName) || sameRequesterName(clientName, primary.name);
    if (aiSpeaksForPrimary) {
        primary.email = primary.email || guardedEmail;
        primary.title = primary.title || stringOrNull(result.clientTitle, 80);
        const aiPhone = stringOrNull(result.clientPhone, 40);
        primary.phone = primary.phone || (aiPhone && aiPhone.replace(/\D/g, '').length >= 8 ? aiPhone : null);
    }
    result.requesters = requesters;
    result.clientName = primary.name;
    result.clientEmail = primary.email;
    result.clientTitle = primary.title;
    // A malformed AI phone stays on the single field for the existing
    // validators to reject or flag; it is never copied into requesters.
    result.clientPhone = primary.phone || (aiSpeaksForPrimary ? result.clientPhone ?? null : null);
    return result;
}

// ---------------------------------------------------------------------------
// Firm hints (consumed by the firm resolver; no storage here)
// ---------------------------------------------------------------------------

// The form's own header labels, in a fixed order. A form's fingerprint is the
// set present, in THIS order, so pdf text that prints labels before values
// gives the same fingerprint. Only the form title and colon-ended header
// field labels count: those are printed whether or not a value is filled.
// Words that can arrive inside a value ("Strata Plan No. 951", "Quote No.
// 4321"), and labels lower on the page that a short text layer can lose
// (Property Affected), are not part of it. v2 (2026-10-13): the v1 set mixed
// both in, so one GK form gave two fingerprints.
const FORM_HEADER_LABELS: ReadonlyArray<readonly [string, RegExp]> = [
    ['CLIENT QUOTATION REQUEST', /\bclient\s+quotation\s+request\b/i],
    ['QUOTATION REQUEST', /\bquotation\s+request\b/i],
    ['QUOTE REQUEST', /\bquote\s+request\b/i],
    ['Issued on behalf of Owners Corporation', /\bissued\s+on\s+behalf\s+of\s+(?:the\s+)?owners\s+corporation\b/i],
    ['Bill To', /\bbill\s+to\s*:/i],
    ['Billing Address', /\bbilling\s+address\s*:/i],
    ['Order No', /\border\s+no\.?\s*:/i],
    ['Quote No', /\bquote\s+no\.?\s*:/i],
    ['Order By', /\border(?:ed)?\s+by\s*:/i],
    ['Order Date', /\border\s+date\s*:/i],
    ['Quote Due', /\bquote\s+due\s*:/i],
    ['No. of Lots', /\bno\.?\s+of\s+lots\s*:/i],
    ['Body Corp Manager', /\bbody\s+corp(?:orate)?\.?\s+manager\s*:/i],
    ['ATTENTION TO', /\battention\s+to\s*:/i],
    ['Agent Strata Manager', /\bagent\s+strata\s+manager\s*:/i],
    ['Assistant Strata Manager', /\bassistant\s+strata\s+manager\s*:/i],
    ['Strata Manager', /\bstrata\s+manager\s*:/i],
    ['Contact for enquiries', /\bcontact\s+for\s+enquiries\s*:/i],
    ['Job Issued By', /\bjob\s+issued\s+by\s*:/i],
    ['Issued Date', /\bissued\s+date\s*:/i],
    ['Site Contact', /\bsite\s+contacts?\s*:/i],
    ['Contact Name', /\bcontact\s+name\s*:/i],
];

function stableHash(text: string): string {
    let first = 0x811c9dc5;
    let second = 0x9e3779b9;
    for (let index = 0; index < text.length; index += 1) {
        const code = text.charCodeAt(index);
        first ^= code;
        first = Math.imul(first, 0x01000193);
        second ^= code + index;
        second = Math.imul(second, 0x85ebca6b);
        second ^= second >>> 13;
    }
    return `${(first >>> 0).toString(16).padStart(8, '0')}${(second >>> 0).toString(16).padStart(8, '0')}`;
}

function validAbn(digits: string): boolean {
    if (!/^\d{11}$/.test(digits)) return false;
    const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
    let sum = 0;
    for (let index = 0; index < 11; index += 1) {
        let digit = Number(digits.charAt(index));
        if (index === 0) digit -= 1;
        sum += digit * (weights[index] as number);
    }
    return sum % 89 === 0;
}

const FIRM_SUFFIX_RE = /\b(?:pty\.?\s*ltd\.?|pty\.?\s*limited|limited|ltd\.?|strata(?:\s+(?:management|managers|services|solutions|specialists|group|partners))?|management|managers|group|partners|services|realty|real\s+estate|properties)$/i;
const NOT_A_FIRM_RE = /\b(?:common|owners|body\s+corp(?:orate)?|strata\s+plan|request|quotation|manager|contractor)\b|[@:]/i;

function firmCandidates(line: string): string[] {
    const labelled = /^(?:bill\s+to|managing\s+agent|agent|client|company)\s*:\s*(.+)$/i.exec(line);
    const segments = (labelled ? [labelled[1] || ''] : line.split(/\t|\s{2,}|\s\|\s/))
        .map((segment) => cleanText(segment).replace(/[.,;]+$/, ''));
    return segments.filter((segment) => {
        const words = segment.split(' ');
        return words.length >= 2 && words.length <= 8
            && /^[A-Z0-9&]/.test(segment)
            && !/\d{3,}/.test(segment)
            && FIRM_SUFFIX_RE.test(segment)
            && !NOT_A_FIRM_RE.test(segment);
    });
}

const FIRM_SUFFIX_WORDS: ReadonlySet<string> = new Set([
    'strata', 'management', 'managers', 'partners', 'services', 'group', 'realty', 'properties', 'pty',
    'ltd', 'limited', 'solutions', 'specialists', 'plus',
]);
// A firm made only of these words ("Strata Management") is a phrase, not a name.
const GENERIC_FIRM_WORDS: ReadonlySet<string> = new Set(['strata', 'management', 'managers', 'services', 'group', 'pty', 'ltd', 'limited']);
const FIRM_BREAK_WORDS: ReadonlySet<string> = new Set([
    'quote', 'request', 'quotation', 'quotations', 'plan', 'owners', 'owner', 'common', 'manager', 'contractor',
    'body', 'bill', 'billing', 'address', 'attention', 'attn', 'job', 'issued', 'order', 'site', 'contact', 'the',
    'of', 'for', 'and', 'to', 'abn', 'acn', 'ph', 'tel', 'fax', 'email', 'mobile', 'level', 'suite', 'po', 'box',
    'street', 'road', 'avenue', 'page', 'please', 'note', 'date', 'due', 'lots', 'name', 'unit', 'details',
    'description', 'dear', 'regards', 'kind', 'hi', 're', 'subject', 'from', 'sent', 'cc', 'thanks', 'thank',
    'your', 'our', 'number', 'no', 'ref', 'reference', 'tax', 'invoice', 'total', 'gst',
]);

/**
 * Firm names in running text, for pdf text that arrives as one line ("...
 * Issued Date: 03/07/2026 Harbour Strata Partners ABN: ..."): a run of
 * capitalised words that ends in a firm word (Strata, Management, Partners,
 * Pty Ltd ...). Labels, job titles, the requesters' own names, digits, state
 * codes and address words end a run; a run is all mixed case or all capitals,
 * with short acronyms ("GK") allowed in either.
 */
function firmRuns(text: string, peopleNames: readonly string[]): string[] {
    const out: string[] = [];
    for (const rawLine of text.split(/\r?\n/)) {
        let line = rawLine
            .replace(new RegExp(ANY_LABEL_GLOBAL_SOURCE, 'gi'), ' | ')
            .replace(new RegExp(`\\b(?:${TITLE_CORE})\\b`, 'gi'), ' | ')
            .replace(COLUMN_GAP_RE, ' | ');
        for (const name of peopleNames) if (name) line = line.split(name).join(' | ');
        let run: string[] = [];
        let mode: 'mixed' | 'caps' | null = null;
        const flush = () => {
            let last = -1;
            run.forEach((token, index) => { if (FIRM_SUFFIX_WORDS.has(token.toLowerCase())) last = index; });
            const firm = last >= 1 ? run.slice(0, last + 1) : [];
            if (firm.length && !firm.every((token) => GENERIC_FIRM_WORDS.has(token.toLowerCase()))) out.push(firm.join(' '));
            run = [];
            mode = null;
        };
        for (const raw of line.split(/\s+/).filter(Boolean)) {
            const endsRun = /[,;]$/.test(raw);
            // "Owners--", "(Strata", "Ltd." -> the word itself.
            const token = raw === '&' ? raw : raw.replace(/^[^A-Za-z0-9]+|[^A-Za-z0-9]+$/g, '');
            const lowered = token.toLowerCase();
            const acronym = token === '&' || /^[A-Z]{1,3}$/.test(token);
            const mixed = /^[A-Z][a-z][A-Za-z&'’-]*$/.test(token);
            const caps = /^[A-Z][A-Z&'’-]{3,}$/.test(token);
            if (FIRM_BREAK_WORDS.has(lowered) || STATE_CODES.has(lowered) || !(acronym || mixed || caps)) {
                flush();
                continue;
            }
            const tokenMode = mixed ? 'mixed' : caps ? 'caps' : null;
            if (tokenMode && mode && tokenMode !== mode) flush();
            if (tokenMode) mode = tokenMode;
            run.push(token);
            if (endsRun) flush();
        }
        flush();
    }
    return out;
}

/**
 * Firm clues in an RFQ: printed firm names, ABNs (checksum-valid), email
 * domains (not free-mail, not the builder's), and a fingerprint of the form's
 * header labels so a business can learn "this layout = this firm" from the
 * operator's matches. Pure; the resolver that stores and learns is elsewhere.
 */
export function rfqFirmHints(text: unknown, options: RequesterTenantContext = {}): RfqFirmHints {
    const source = typeof text === 'string' ? text : '';
    const tenantDomains = lower(options.tenantDomains);
    const tenantNames = lower(options.tenantNames).map((name) => name.replace(/[^a-z0-9]/g, ''));
    const tenantAbns = (Array.isArray(options.tenantAbns) ? options.tenantAbns : [])
        .map((abn) => String(abn || '').replace(/\D/g, ''));

    // The builder's own firm: its legal or trading name, or a name that starts
    // with its email domain's label ("rjbirdbuilding.com.au" -> "RJ Bird
    // Building Pty Ltd"), for a profile with no legal name on it.
    const tenantDomainLabels = tenantDomains
        .map((domain) => (domain.split('.')[0] || '').replace(/[^a-z0-9]/g, ''))
        .filter((label) => label.length >= 5);
    const isTenantFirm = (key: string) => tenantNames.some((name) => name && (key.startsWith(name) || name.startsWith(key)))
        || tenantDomainLabels.some((label) => key.startsWith(label));

    const lineCandidates = source.split(/\r?\n/).flatMap((line) => firmCandidates(cleanText(line)));
    const siteContacts = extractRfqSiteContacts(source, options);
    // A site contact's own email (a tenant's or owner's) says nothing about the firm.
    const siteEmails = new Set(siteContacts.map((contact) => contact.email).filter(Boolean));
    const people = [
        ...extractRfqRequesters(source, options).map((requester) => requester.name),
        ...siteContacts.map((contact) => contact.name),
    ];
    const firmNames: string[] = [];
    const firmKeys = new Set<string>();
    for (const candidate of [...lineCandidates, ...firmRuns(source, people)]) {
        const key = candidate.toLowerCase().replace(/[^a-z0-9]/g, '');
        if (!key || firmKeys.has(key) || isTenantFirm(key)) continue;
        firmKeys.add(key);
        firmNames.push(candidate);
    }

    const abns: string[] = [];
    const abnRe = /\bA\.?\s?B\.?\s?N\.?\s*[:#.-]?\s*((?:\d[\s-]?){11})/gi;
    let abnMatch: RegExpExecArray | null;
    while ((abnMatch = abnRe.exec(source)) !== null) {
        const digits = (abnMatch[1] || '').replace(/\D/g, '');
        if (validAbn(digits) && !tenantAbns.includes(digits) && !abns.includes(digits)) abns.push(digits);
    }

    const emailDomains: string[] = [];
    for (const { email } of emailMatches(source)) {
        const domain = splitEmail(email).domain;
        if (!domain || FREE_MAIL_DOMAINS.has(domain) || domainIsTenant(domain, tenantDomains)) continue;
        if (lower(options.tenantStaffEmails).includes(email.toLowerCase())) continue;
        if (siteEmails.has(email.toLowerCase())) continue;
        if (!emailDomains.includes(domain)) emailDomains.push(domain);
    }

    const labels = FORM_HEADER_LABELS.filter(([, re]) => re.test(source)).map(([label]) => label);
    const formFingerprint = labels.length >= 2 ? `rfq-form-v2:${stableHash(labels.join('|'))}` : null;

    return { firmNames, abns, emailDomains, formFingerprint };
}

/** Maps the tenant profile (tenant-resolver getInstanceOwner) to the exclusion context. */
export function requesterTenantContextFromOwner(owner: unknown): RequesterTenantContext {
    if (!owner || typeof owner !== 'object') return {};
    const record = owner as Record<string, unknown>;
    const list = (value: unknown): string[] => (Array.isArray(value) ? value.map((entry) => String(entry || '')).filter(Boolean) : []);
    return {
        tenantDomains: list(record.emailDomains),
        tenantStaffNames: list(record.staffNames),
        tenantStaffEmails: list(record.staffEmails),
        tenantNames: [record.legalName, record.tradingName].map((name) => cleanText(name)).filter(Boolean),
        tenantAbns: record.abn ? [String(record.abn)] : [],
    };
}
